import Vue from 'vue'
import axios from 'axios'
import router from '@/router'
import qs from 'qs'
import { checkPathIsNoNeedToLogin, checkIsLoginValid } from '../js/utils'
import i18n from '@/lang/i18n.js'
import * as Sentry from '@sentry/browser'

/*
createAxiosInstance 狀態處理
401, 403, 404, 409, 500，這裡都會直接handle掉不用管，唯獨400較多狀況
遇到400的處理方式：
1. 看有沒有userHandlesErrors欄位，若為true，你需要自己做後續處理
2. 如果後端有給錯誤ErrorMessage.Description，則優先使用後端給的來跳出
3. 如果後端沒給 or 完全預期外錯誤，嘗試使用前端config中傳入的訊息欄位
4. 後端沒給、前端打api沒有附上config：發出「請稍後再試」
config格式如下 (打api時傳入)
  config = {
    metadata: { // 下面的欄位依據自己需要填寫即可，都非必填
      userHandlesErrors: false, // 是否自定義處理錯誤，不由instance處理
      customMsg: {
        success: "", // API正常時的toast訊息
        error: "" // API回400時的toast訊息
      }
    }
  }
*/

const createAxiosInstance = (baseURL) => {
  const instance = axios.create({
    baseURL
  })
  instance.defaults.paramsSerializer = (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  }
  // 避免重複出現 Alert
  const genAlertIfNotExist = (msg, finallyCallback) => {
    // eslint-disable-next-line eqeqeq
    if (document.getElementsByClassName('v-modal').length == 0) {
      Vue.prototype
        .$alert(msg, '警告', {
          confirmButtonText: i18n.t('General.Confirm'),
          type: 'warning',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false
        }).finally(() => {
          finallyCallback()
        })
    }
  }
  // 401 重新登入
  const alertUnauthorized = () => {
    genAlertIfNotExist(i18n.t('Auth.Unauthorized'), () => {
      router.push({ name: 'LoginMain' })
    })
  }
  // 403 沒有啟用此服務
  const alertForbidden = () => {
    genAlertIfNotExist(i18n.t('Auth.Forbidden'), () => {
      Vue.prototype.$toMainPage()
    })
  }
  // 403 非最高管理者
  const alertNotHighestAdmin = () => {
    genAlertIfNotExist(i18n.t('Auth.NotHighestAdmin'))
  }
  // 404 有可能為伺服器重新部署中、 409 為資料寫入衝突，應過一陣子就可以了
  const alertTryAgainLater = (errorMessage = i18n.t('Server.TryAgainLater')) => {
    Vue.prototype.$message.error(errorMessage)
  }
  // 500
  const alertServerError = (errorMessage = i18n.t('Server.ServerError')) => {
    Vue.prototype.$message.error(errorMessage)
  }
  instance.interceptors.request.use(
    (config) => {
      const newConfig = { ...config }
      if (!checkPathIsNoNeedToLogin(window.location.href) && !checkIsLoginValid()) {
        // 需要登入且驗證失敗則重新整理、將出現阻擋 Alert，且於確認後重新導向至登入頁面
        location.reload()
      }
      let webCookie = null
      try {
        webCookie = Vue.prototype.$getCookie('comLoginToken').split('token=').pop()
      } catch (error) {
        console.log(error)
      }
      const xCorrelationId = Vue.prototype.$getGuid()
      newConfig.headers = {
      // 預設資料
        Authorization: webCookie,
        Kingnetauth: `Cookies ${webCookie}`,
        'X-Correlation-Id': xCorrelationId,
        // 客製資料
        // 若有客製資料則以客製資料覆寫預設資料，以避免取用到預設的 Authorization  (e.g. smartBuildingSystemAPI.js 中供廠商巡檢用的 API)
        ...newConfig.headers
      }
      Sentry.setTag('X-Correlation-Id', xCorrelationId)
      // 如果開發者有傳入自定義config，則metadata會已經存在，若沒有則需創一個新的
      if (newConfig.metadata) {
        newConfig.metadata.startTime = new Date()
      } else {
        newConfig.metadata = { startTime: new Date() }
      }
      return newConfig
    },
    (err) => {
      return Promise.reject(err)
    }
  )
  // 先照舊，之後再整合多個 Alert 的 Issue
  instance.interceptors.response.use(
    res => {
      const newRes = { ...res }
      newRes.config.metadata.endTime = new Date()
      newRes.duration =
        newRes.config.metadata.endTime - newRes.config.metadata.startTime

      // 如果有給成功訊息就跳出提醒
      if (res.config?.metadata?.customMsg?.success) {
        Vue.prototype.$message.success(res.config.metadata.customMsg.success)
      }

      return newRes
    },
    async (err) => {
      const newErr = { ...err }
      newErr.config.metadata.endTime = new Date()
      newErr.duration =
        newErr.config.metadata.endTime - newErr.config.metadata.startTime
      if (newErr && newErr.response && newErr.response.status) {
        const { status } = newErr.response
        switch (status) {
          case 401:
            alertUnauthorized()
            break
          case 403:
            if (newErr.response.config.url.includes('SwitchNotification')) {
              alertNotHighestAdmin()
            } else {
              alertForbidden()
            }
            break
          case 404:
          case 409:
            alertTryAgainLater()
            break
          case 500:
            alertServerError()
            break
          default: {
            if (newErr?.config?.metadata?.userHandlesErrors) {
              break
            }

            let errMsg = ''

            // 先看看後端有沒有給錯誤訊息
            try {
              errMsg = await getApiErrorMsg(newErr)
            } catch (error) {
              console.log(error)
            }

            // 後端沒給，但若前端config有給自定義錯誤訊息，則使用
            if (!errMsg && newErr?.config?.metadata?.customMsg?.error) {
              errMsg = newErr.config.metadata.customMsg.error
            }

            if (errMsg) {
              Vue.prototype.$message.error(errMsg)
            } else {
              // 跳出籠統的「請稍後再試」文案
              alertTryAgainLater()
            }
            break
          }
        }
      }
      return Promise.reject(newErr)
    }
  )
  return instance
}

// 取得錯誤訊息
const getApiErrorMsg = async (err) => {
  try {
    // Blob不能直接取值
    if (err.response.data instanceof Blob) {
      const text = await err.response.data.text()
      const json = JSON.parse(text)
      return json.ErrorMessage.Description
    }

    return err.response.data.ErrorMessage.Description
  } catch (error) {
    console.log(error)
    return ''
  }
}

export default createAxiosInstance
